import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../theme';

export interface AvatarUser {
  userId: string;
  displayName: string;
  photoURL?: string;
}

interface AvatarStackProps {
  users: AvatarUser[];
  totalCount: number;
  size?: number;
}

const MAX_VISIBLE = 5;
const FALLBACK_COLORS = [Colors.turquoise, Colors.sunset, Colors.purple, '#22c55e', '#f59e0b'];

function getInitial(name: string): string {
  return (name || '?').charAt(0).toUpperCase();
}

const Avatar: React.FC<{ user: AvatarUser; size: number; colorIndex: number }> = React.memo(({
  user,
  size,
  colorIndex,
}) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const hasPhoto = Boolean(user.photoURL) && !imgFailed;
  const fallbackColor = FALLBACK_COLORS[colorIndex % FALLBACK_COLORS.length];

  return (
    <View
      style={[
        styles.ring,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: hasPhoto ? 'transparent' : fallbackColor,
        },
      ]}
    >
      {hasPhoto ? (
        <Image
          source={{ uri: user.photoURL }}
          style={[styles.img, { borderRadius: size / 2 }]}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <Text style={[styles.initial, { fontSize: size * 0.42 }]}>
          {getInitial(user.displayName)}
        </Text>
      )}
    </View>
  );
});

const AvatarStack: React.FC<AvatarStackProps> = React.memo(({ users, totalCount, size = 26 }) => {
  const visible = users.slice(0, MAX_VISIBLE);
  const overflow = Math.max(0, totalCount - visible.length);
  const overlap = Math.round(size * 0.34);

  if (visible.length === 0 && totalCount === 0) {
    return (
      <View style={styles.row}>
        {[0, 1, 2].map(i => (
          <View
            key={i}
            style={[
              styles.ring,
              styles.emptyDot,
              { width: size, height: size, borderRadius: size / 2, marginLeft: i === 0 ? 0 : -overlap },
            ]}
          />
        ))}
      </View>
    );
  }

  return (
    <View style={styles.row}>
      {visible.map((user, i) => (
        <View
          key={user.userId}
          style={{ marginLeft: i === 0 ? 0 : -overlap, zIndex: MAX_VISIBLE - i }}
        >
          <Avatar user={user} size={size} colorIndex={i} />
        </View>
      ))}
      {overflow > 0 && (
        <View
          style={[
            styles.ring,
            styles.overflowBadge,
            { width: size, height: size, borderRadius: size / 2, marginLeft: -overlap },
          ]}
        >
          <Text style={[styles.overflowText, { fontSize: size * 0.34 }]}>+{overflow}</Text>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  ring: {
    borderWidth: 1.5,
    borderColor: 'rgba(5,8,18,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  img: { width: '100%', height: '100%' },
  initial: { color: '#fff', fontWeight: '700' },
  emptyDot: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderColor: 'rgba(255,255,255,0.22)',
  },
  overflowBadge: {
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderColor: 'rgba(5,8,18,0.92)',
  },
  overflowText: { color: 'rgba(255,255,255,0.72)', fontWeight: '700' },
});

export default AvatarStack;
